import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, MessageCircle, ArrowLeft, ArrowUpRight, Palmtree, Waves, Sun,
  MapPin, Anchor, Sparkles, Compass, Clock, ShieldCheck,
} from "lucide-react";
import SEO from "@/components/SEO";
import Header from "@/components/sections/Header";
import Reviews from "@/components/sections/Reviews";
import Footer from "@/components/sections/Footer";
import StickyCallBar from "@/components/StickyCallBar";
import WhatsAppFab from "@/components/WhatsAppFab";
import { PHONE_DISPLAY, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
import { track } from "@/lib/analytics";
import {
  localBusinessSchema, breadcrumbSchema, serviceSchema, faqPageSchema,
} from "@/lib/schemas";
import { SITE_URL } from "@/lib/site";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { fadeUp, fadeUpStagger, IN_VIEW, EASE_OUT_EXPO } from "@/lib/motion";

const SLUG = "recupero-auto-spiaggia-ostia-fregene-terracina";
const CANONICAL = `${SITE_URL}/${SLUG}`;

const TITLE =
  "Recupero Auto in Spiaggia · Ostia, Fregene, Terracina H24 | 06 9763 6222";
const DESCRIPTION =
  "Auto insabbiata o incastrata in spiaggia a Ostia, Fregene, Fiumicino, Terracina o litorale laziale? Recupero rapido H24 con mezzi adatti alla sabbia. Chiama 06 9763 6222.";
const KEYWORDS =
  "recupero auto spiaggia, auto insabbiata Ostia, auto insabbiata Fregene, carro attrezzi Terracina, auto sabbia litorale Roma, recupero auto sabbia H24, auto bloccata spiaggia Lazio";

const H1 = "Recupero auto insabbiata — Ostia, Fregene, Terracina H24";
const LEAD =
  "Auto rimasta incastrata nella sabbia in spiaggia? Ruota affondata sul lungomare? Interveniamo H24 con mezzi adatti a lavorare su fondo cedevole e ti riportiamo in strada in sicurezza — senza rovinare il veicolo e senza aspettare l'estate finita.";

const BEACHES = [
  {
    name: "Ostia",
    detail: "Lungomare Duilio, Lido di Castel Fusano, Ostia Levante, Ostia Ponente, Idroscalo, Cristoforo Colombo.",
  },
  {
    name: "Fregene",
    detail: "Fregene Nord, Fregene Sud, lungomare di Ponente, viale Nettuno e stabilimenti balneari.",
  },
  {
    name: "Fiumicino",
    detail: "Isola Sacra, Passoscuro, Focene, Maccarese, Fregene Nord, aree dune e canneti.",
  },
  {
    name: "Terracina",
    detail: "Lungomare Circe, Terracina Levante e Ponente, La Fiora, Borgo Hermada, Foce Sisto.",
  },
  {
    name: "Sabaudia",
    detail: "Lungomare, La Bufalara, dune di Sabaudia (accessi consentiti), Torre Paola.",
  },
  {
    name: "San Felice Circeo & Sperlonga",
    detail: "Interventi su richiesta lungo tutto il litorale pontino, spiagge libere e accessi carrabili.",
  },
];

const HOW_STEPS = [
  {
    n: "01",
    icon: Phone,
    title: "Ci chiami e ci dai la posizione precisa",
    body:
      "Meglio se hai la posizione GPS o il nome dello stabilimento. Ti chiediamo se l'auto è sulla sabbia asciutta, umida, vicino all'acqua o incastrata su dune.",
  },
  {
    n: "02",
    icon: Compass,
    title: "Scegliamo il mezzo giusto",
    body:
      "In base al fondo (sabbia, dune, ghiaia mista) e alla vicinanza all'acqua, mandiamo il carro attrezzi con l'attrezzatura adatta a non aggravare l'insabbiamento.",
  },
  {
    n: "03",
    icon: Anchor,
    title: "Recuperiamo l'auto senza danneggiarla",
    body:
      "Estrazione controllata, tavole di appoggio se serve, gancio protetto sul telaio. Riportiamo il veicolo in strada asfaltata e — se lo chiedi — la controlliamo prima di lasciarti.",
  },
  {
    n: "04",
    icon: ShieldCheck,
    title: "Fattura e documentazione",
    body:
      "Al termine ti rilasciamo fattura regolare con dettaglio dell'intervento, utile anche se vuoi richiedere un eventuale rimborso alla tua polizza (secondo le condizioni della tua compagnia).",
  },
];

const CASI = [
  {
    icon: Sun,
    title: "Ferragosto e weekend d'estate",
    body:
      "Stabilimento pieno, auto parcheggiate su fondo cedevole, marea in arrivo: interveniamo anche in giornate ad alta pressione.",
  },
  {
    icon: Waves,
    title: "Auto vicino alla battigia",
    body:
      "Ruote affondate a ridosso dell'acqua? Muoviamoci in fretta prima che la marea peggiori la situazione.",
  },
  {
    icon: Palmtree,
    title: "Fuoristrada o SUV su dune",
    body:
      "Impostati sulle riduzioni sbagliate o senza gonfiaggio corretto: recuperiamo anche fuoristrada e camper impantanati.",
  },
  {
    icon: Sparkles,
    title: "Auto dopo mareggiata o pioggia",
    body:
      "La spiaggia dopo un temporale diventa una trappola: sabbia bagnata + terreno cedevole. Arriviamo attrezzati.",
  },
];

const FAQS = [
  {
    q: "Riuscite a raggiungere l'auto anche se è in spiaggia libera?",
    a: "Sì. Valutiamo al telefono l'accesso più sicuro (varco carrabile, discesa, passaggio dietro gli stabilimenti). Se necessario, coordiniamoci con te sul punto per il recupero più vicino possibile all'auto.",
  },
  {
    q: "L'auto rischia di essere danneggiata durante il recupero in sabbia?",
    a: "Lavoriamo con ganci di traino protetti e punti di attacco al telaio dedicati per la tua vettura. Se il veicolo è profondamente insabbiato o vicino all'acqua, procediamo per gradi utilizzando anche tavole di appoggio, per evitare torsioni o strappi.",
  },
  {
    q: "Intervenite anche di notte o al mattino presto?",
    a: "Sì, siamo operativi 24 ore su 24, 7 giorni su 7. Molti recuperi in spiaggia si fanno all'alba o dopo il tramonto per lavorare in sicurezza fuori dagli orari di massima affluenza.",
  },
  {
    q: "Coprite anche Terracina, Sabaudia e il litorale pontino?",
    a: "Sì. Copriamo tutto il litorale laziale: Ostia, Fregene, Fiumicino, Passoscuro, Maccarese, Torvaianica, Anzio, Nettuno, Sabaudia, Terracina, San Felice Circeo e Sperlonga. Per zone molto lontane concordiamo tempi e costi al telefono.",
  },
  {
    q: "Quanto costa un recupero in spiaggia?",
    a: "Dipende dalla distanza dal punto di intervento, dal grado di insabbiamento e dal tipo di veicolo (utilitaria, SUV, camper, fuoristrada). Al telefono ti diamo un preventivo chiaro prima di partire, senza costi nascosti sul posto.",
  },
  {
    q: "Se sono in vacanza e non ho documenti dell'auto con me?",
    a: "Nessun problema per il recupero. Ci basta la tua identificazione al momento dell'intervento e i dati della polizza / libretto quando emettiamo la fattura. Ti aiutiamo a gestire tutto sul posto.",
  },
  {
    q: "Il rimborso lo copre la mia assicurazione?",
    a: "Il rimborso non è mai automatico e dipende esclusivamente dalla tua polizza (massimali, franchigie, coperture per soccorso stradale, obbligo di autorizzazione preventiva). Ti forniamo fattura e documentazione dell'intervento: sarà la tua compagnia a valutare in base al contratto sottoscritto.",
  },
];

export default function BeachRecoveryPage() {
  const jsonLd = [
    localBusinessSchema({ extraAreas: ["Terracina", "Sabaudia", "San Felice Circeo", "Fregene"] }),
    breadcrumbSchema([
      { name: "Home", url: `${SITE_URL}/` },
      { name: "Recupero auto in spiaggia — Ostia, Fregene, Terracina", url: CANONICAL },
    ]),
    serviceSchema({
      name: "Recupero auto insabbiata — Litorale laziale H24",
      description:
        "Servizio di recupero auto insabbiata H24 sul litorale del Lazio: Ostia, Fregene, Fiumicino, Terracina, Sabaudia, San Felice Circeo, Sperlonga. Estrazione controllata con mezzi adatti al fondo cedevole, fattura e documentazione dettagliata rilasciata al termine.",
      url: CANONICAL,
      areaServed: "Litorale del Lazio",
    }),
    faqPageSchema(FAQS),
  ];

  return (
    <div
      className="min-h-screen bg-white text-navy"
      data-testid={`page-${SLUG}`}
    >
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        keywords={KEYWORDS}
        image={`${SITE_URL}/og-image.png`}
        jsonLd={jsonLd}
      />

      <Header />

      <main>
        {/* HERO — orange-forward, distinct from Insurance page */}
        <section
          data-testid="beach-hero"
          className="relative isolate min-h-[86vh] flex items-center overflow-hidden bg-navy grain"
        >
          {/* Warm dune gradient (unique from other pages) */}
          <div className="absolute inset-0 -z-20 bg-gradient-to-b from-[#0B192C] via-[#0B192C] to-[#0f2540]" />
          <div className="absolute inset-0 -z-10 opacity-70">
            <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-brand-orange/30 blur-[160px]" />
            <div className="absolute top-1/3 -right-32 h-[420px] w-[420px] rounded-full bg-[#f7c07a]/20 blur-[140px]" />
          </div>
          {/* SVG wave line — signature beach motif */}
          <svg
            aria-hidden="true"
            className="absolute inset-x-0 bottom-[6%] w-full h-24 opacity-30 -z-10"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,60 C150,110 300,20 450,60 C600,100 750,20 900,55 C1050,90 1150,45 1200,60 L1200,120 L0,120 Z"
              fill="url(#sandgrad)"
            />
            <defs>
              <linearGradient id="sandgrad" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#FF6B00" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#f7c07a" stopOpacity="0.3" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 -z-10 hero-grid opacity-20" />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-28 md:pt-32 pb-16 md:pb-24 w-full relative">
            <Link
              to="/"
              data-testid="beach-back-link"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold"
            >
              <ArrowLeft className="h-4 w-4" /> Torna alla home
            </Link>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUpStagger}
              className="mt-6 max-w-4xl"
            >
              <motion.span
                variants={fadeUp}
                className="inline-flex items-center gap-2 rounded-full bg-white/8 ring-1 ring-white/20 px-3 py-1.5 text-xs sm:text-sm font-semibold tracking-[0.22em] uppercase text-white/90 backdrop-blur"
              >
                <Palmtree className="h-3.5 w-3.5 text-brand-orange" />
                Litorale Laziale · H24
              </motion.span>

              <motion.h1
                variants={fadeUp}
                data-testid="beach-h1"
                className="mt-6 font-display font-black text-white text-shadow-strong text-4xl sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tight"
              >
                Auto insabbiata?{" "}
                <span className="text-brand-orange italic">
                  Ti tiriamo fuori
                </span>{" "}
                dalla sabbia.
              </motion.h1>

              <motion.p
                variants={fadeUp}
                data-testid="beach-lead"
                className="mt-6 text-white/85 text-base sm:text-xl max-w-3xl leading-relaxed"
              >
                {LEAD}
              </motion.p>

              {/* Beach chips */}
              <motion.div variants={fadeUp} className="mt-7 flex flex-wrap gap-2">
                {["Ostia", "Fregene", "Fiumicino", "Terracina", "Sabaudia", "San Felice Circeo"].map(
                  (b) => (
                    <span
                      key={b}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/8 ring-1 ring-white/15 px-3 py-1 text-xs sm:text-sm text-white/90 font-medium"
                    >
                      <MapPin className="h-3 w-3 text-brand-orange" />
                      {b}
                    </span>
                  )
                )}
              </motion.div>

              <motion.div variants={fadeUp} className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href={PHONE_TEL_HREF}
                  onClick={() => track.phoneCall("beach_hero")}
                  data-testid="beach-call-button"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-brand-orange text-white font-bold px-6 py-4 text-lg sm:text-xl animate-pulse-ring btn-glow-orange hover:brightness-110 hover:-translate-y-0.5 transition"
                >
                  <Phone className="h-6 w-6" />
                  Chiama Ora
                  <span className="font-mono">{PHONE_DISPLAY}</span>
                </a>
                <a
                  href={WHATSAPP_HREF}
                  onClick={() => track.whatsapp("beach_hero")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="beach-whatsapp-button"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-whatsapp text-white font-bold px-6 py-4 text-lg sm:text-xl btn-glow-green hover:brightness-110 hover:-translate-y-0.5 transition"
                >
                  <MessageCircle className="h-6 w-6" />
                  WhatsApp
                </a>
              </motion.div>

              <motion.p
                variants={fadeUp}
                className="mt-5 text-white/50 text-xs sm:text-sm font-mono tracking-wide"
              >
                Mezzi adatti al fondo cedevole · Fattura sempre inclusa
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* HOW — 4 step */}
        <section
          id="come-funziona"
          data-testid="beach-steps-section"
          className="relative py-20 md:py-28 bg-white"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <motion.header
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="max-w-3xl"
            >
              <motion.p variants={fadeUp} className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">
                — Come recuperiamo
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.02]"
              >
                Estrazione <span className="text-brand-orange italic">controllata</span>,{" "}
                senza forzature.
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="mt-5 text-muted-foreground text-base sm:text-lg leading-relaxed"
              >
                Ogni tipo di sabbia — asciutta, umida, dune, mista a ghiaia — richiede
                un approccio diverso. Non tiriamo mai a strappo: valutiamo il fondo,
                l'accessibilità e la posizione dell'auto prima di attaccare il gancio.
              </motion.p>
            </motion.header>

            <ol className="mt-14 grid gap-4 md:gap-5 md:grid-cols-2 lg:grid-cols-4">
              {HOW_STEPS.map((s, i) => (
                <motion.li
                  key={s.n}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={IN_VIEW}
                  transition={{ duration: 0.75, ease: EASE_OUT_EXPO, delay: 0.05 * i }}
                  data-testid={`beach-step-${i}`}
                  className="relative rounded-2xl border border-border bg-white p-6 hover:shadow-lg hover:border-brand-orange/40 transition"
                >
                  <span className="font-mono text-xs tracking-widest text-muted-foreground">{s.n}</span>
                  <div className="mt-3 grid place-items-center h-12 w-12 rounded-xl bg-navy text-white">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display font-extrabold text-lg text-navy leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {s.body}
                  </p>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        {/* BEACHES COVERED — asymmetric editorial layout */}
        <section
          id="litorali"
          data-testid="beach-areas-section"
          className="relative py-20 md:py-28 bg-navy text-white overflow-hidden grain"
        >
          <div className="absolute inset-0 hero-grid opacity-20 pointer-events-none" />
          <div className="absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-brand-orange/15 blur-[140px] pointer-events-none" />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 relative">
            <motion.header
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="grid md:grid-cols-12 gap-6 md:gap-10 items-end"
            >
              <div className="md:col-span-7">
                <motion.p variants={fadeUp} className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">
                  — Litorale coperto
                </motion.p>
                <motion.h2
                  variants={fadeUp}
                  className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
                >
                  Da <span className="text-brand-orange italic">Ostia</span> a{" "}
                  <span className="text-brand-orange italic">Sperlonga</span>.
                </motion.h2>
              </div>
              <motion.p
                variants={fadeUp}
                className="md:col-span-5 text-white/70 text-base sm:text-lg leading-relaxed"
              >
                Ci muoviamo lungo tutto il litorale del Lazio. Se non trovi il tuo tratto
                qui sotto, chiamaci comunque: quasi certamente arriviamo.
              </motion.p>
            </motion.header>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="mt-12 grid gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3"
            >
              {BEACHES.map((b, i) => (
                <motion.article
                  key={b.name}
                  variants={fadeUp}
                  data-testid={`beach-area-${i}`}
                  className="group relative rounded-2xl bg-white/5 backdrop-blur ring-1 ring-white/10 p-6 hover:bg-white/10 hover:ring-brand-orange/40 transition"
                >
                  <span className="absolute top-4 right-5 font-mono text-xs text-white/40">
                    0{i + 1}
                  </span>
                  <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-orange/20 text-brand-orange">
                    <Palmtree className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display font-extrabold text-white text-xl">
                    {b.name}
                  </h3>
                  <p className="mt-2 text-white/70 text-sm leading-relaxed">
                    {b.detail}
                  </p>
                </motion.article>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={IN_VIEW}
              transition={{ duration: 0.85, ease: EASE_OUT_EXPO }}
              className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <a
                href={PHONE_TEL_HREF}
                onClick={() => track.phoneCall("beach_areas")}
                data-testid="beach-areas-call-button"
                className="inline-flex items-center gap-3 rounded-2xl bg-brand-orange text-white px-6 py-4 font-bold text-base sm:text-lg hover:brightness-110 hover:-translate-y-0.5 transition btn-glow-orange"
              >
                <Phone className="h-5 w-5" />
                Chiama {PHONE_DISPLAY}
              </a>
              <span className="text-white/50 text-sm font-mono tracking-widest uppercase">
                — Litorale coperto tutto l'anno
              </span>
            </motion.div>
          </div>
        </section>

        {/* CASI TIPICI */}
        <section
          data-testid="beach-cases-section"
          className="relative py-20 md:py-28 bg-[color:var(--brand-surface)]"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <motion.header
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="max-w-3xl"
            >
              <motion.p variants={fadeUp} className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">
                — Casi tipici
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="mt-4 font-display font-black text-4xl sm:text-5xl text-navy tracking-tight leading-[1.02]"
              >
                Quattro situazioni in cui{" "}
                <span className="text-brand-orange italic">ci chiamano</span>.
              </motion.h2>
            </motion.header>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="mt-12 grid gap-4 md:grid-cols-2"
            >
              {CASI.map((c, i) => (
                <motion.div
                  key={c.title}
                  variants={fadeUp}
                  data-testid={`beach-case-${i}`}
                  className="flex items-start gap-4 rounded-2xl bg-white border border-border p-6 hover:shadow-md transition"
                >
                  <div className="grid place-items-center h-12 w-12 rounded-xl bg-brand-orange/10 text-brand-orange shrink-0">
                    <c.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-display font-extrabold text-lg text-navy">{c.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section
          id="faq"
          data-testid="beach-faq-section"
          className="relative py-20 md:py-28 bg-white"
        >
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="text-center max-w-2xl mx-auto"
            >
              <motion.p variants={fadeUp} className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">
                — Domande frequenti
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="mt-4 font-display font-black text-4xl sm:text-5xl text-navy tracking-tight leading-[1.02]"
              >
                Tutto quello che ti serve <span className="text-brand-orange italic">sapere</span>.
              </motion.h2>
            </motion.div>

            <Accordion
              type="single"
              collapsible
              className="mt-10 divide-y divide-border border border-border rounded-2xl bg-white"
              data-testid="beach-faq-accordion"
            >
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="px-5 sm:px-7">
                  <AccordionTrigger
                    data-testid={`beach-faq-trigger-${i}`}
                    className="text-left font-display font-bold text-navy text-base sm:text-lg hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-10 text-center">
              <p className="text-muted-foreground">
                Sei in spiaggia adesso e l'auto non si muove?
              </p>
              <a
                href={PHONE_TEL_HREF}
                onClick={() => track.phoneCall("beach_faq")}
                data-testid="beach-faq-call-button"
                className="mt-3 inline-flex items-center gap-3 rounded-2xl bg-navy text-white px-6 py-4 font-bold hover:bg-brand-orange transition-colors"
              >
                <Phone className="h-5 w-5" />
                Chiama {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>

        <Reviews />
      </main>

      <Footer />
      <WhatsAppFab />
      <StickyCallBar />
      <div aria-hidden className="h-20 md:h-0" />
    </div>
  );
}
